import type { CreateOrganizationRequest, IOrganization } from '@/domain/entities/organization';
import { isNotFoundError } from '@/lib';
import { getApiEndpoints } from '@/services/api/endpoints';
import type { CreateItemResponse } from '@/services/api/types';
import { createAppAsyncThunk } from '@/store/typedAsyncThunk';
import type {
  CheckSlugResult,
  CreateOrganizationResult,
  UpdateOrganizationMemberRequest,
  UpdateOrganizationMemberResponse,
} from './organizationTypes';
import type { IOrganizationMember } from '@/domain/entities/org-member';

function toSlugKey(slug: string) {
  return slug.trim().toLowerCase();
}

// Fetch organization by orgId
export const fetchOrganization = createAppAsyncThunk<IOrganization, { orgId: string }>(
  'organizations/fetchOrganization',
  async ({ orgId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.organizations.getOrganization}?id=${orgId}`;
      logger?.info('[fetchOrganization] Fetching organization', { endpoint, orgId });

      const response = await apiService.get<IOrganization>(endpoint);
      logger?.debug('[fetchOrganization] Response received', { orgId });

      return response;
    } catch (err) {
      const error = err as Error;
      logger?.error('[fetchOrganization] Failed to fetch organization', { orgId, message: error.message });
      return rejectWithValue({ message: error.message });
    }
  },
  {
    condition: ({ orgId }, { getState }) => {
      const id = orgId.trim();
      if (!id) return false;

      const state = getState().organizations;
      if (state.items.some((organization) => organization.id === id)) return false;
      if (state.fetchingById?.[id]) return false;
      return true;
    },
  },
);

export const listOrganizations = createAppAsyncThunk<IOrganization[], void>(
  'organizations/listOrganizations',
  async (_, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.organizations.getOrganization}`;
      logger?.info('[listOrganizations] Fetching from:', endpoint);

      const response = await apiService.get<IOrganization[]>(endpoint);
      logger?.info('[listOrganizations] Response:', response);

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[listOrganizations] Error:', error.message);
      if (isNotFoundError(error)) {
        return [];
      }
      return rejectWithValue({ message: error.message });
    }
  },
);

/**
 * Return all members (users) of an organization, including the owner.
 */
export const fetchOrgMemberships = createAppAsyncThunk<IOrganizationMember[], { orgId: string }>(
  'organizations/fetchOrganizationMembers',
  async ({ orgId }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.organizationMembers.getOrganizationMembers}?orgID=${orgId}`;
      logger?.info('[fetchOrganizationMembers] Fetching from:', endpoint);

      const response = await apiService.get<IOrganizationMember[]>(endpoint);
      logger?.info('[fetchOrganizationMembers] Response:', response);

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchOrganizationMembers] Error:', error.message);
      if (isNotFoundError(error)) {
        return [];
      }
      return rejectWithValue({ message: error.message });
    }
  },
);

/**
 * Return all organizations that a user belongs to, including the one they own.
 */
export const fetchUserMemberships = createAppAsyncThunk<IOrganizationMember[], { memberID: string }>(
  'organizations/fetchUserMemberships',
  async ({ memberID }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      const endpoint = `${getApiEndpoints().core.organizationMembers.getOrganizationMembers}?memberID=${memberID}`;
      logger?.info('[fetchUserMemberships] Fetching from:', endpoint);

      const response = await apiService.get<IOrganizationMember[]>(endpoint);
      logger?.info('[fetchUserMemberships] Response:', response);

      return response;
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };
      logger?.error('[fetchUserMemberships] Error:', error.message);
      if (isNotFoundError(error)) {
        return [];
      }
      return rejectWithValue({ message: error.message });
    }
  },
);

/**
 * Update an organization member's role or status. Only org owner can do this.
 */

export const updateOrganizationMember = createAppAsyncThunk<
  UpdateOrganizationMemberResponse,
  UpdateOrganizationMemberRequest
>('organizations/updateOrganizationMember', async (body, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    const endpoint = getApiEndpoints().core.organizationMembers.updateOrganizationMember;
    logger?.info('[updateOrganizationMember] Updating organization member', {
      orgID: body.orgID,
      memberID: body.memberID,
      hasStatus: Boolean(body.status),
      hasRoleKey: Boolean(body.roleKey),
      hasCreateRole: Boolean(body.createRole),
    });

    return await apiService.put<UpdateOrganizationMemberResponse>(endpoint, body);
  } catch (err) {
    const error = err as Error;
    logger?.error('[updateOrganizationMember] Error:', error.message);
    return rejectWithValue({ message: error.message });
  }
});

export const createOrganization = createAppAsyncThunk<
  CreateItemResponse<CreateOrganizationResult>,
  { item: CreateOrganizationRequest }
>('organizations/createUserOrganization', async ({ item }, { extra: { config }, rejectWithValue }) => {
  const { apiService, logger } = config;

  try {
    logger?.info('[createUserOrganization] Starting organization creation');

    const endpoint = getApiEndpoints().core.organizations.createOrganization;
    const request: CreateOrganizationRequest = {
      name: item.name,
      title: item.title,
      slug: item.slug,
      website: item.website,
      email: item.email,
      phone: item.phone,
      address: item.address,
    };
    return await apiService.post<CreateItemResponse<CreateOrganizationResult>>(endpoint, request);
  } catch (err) {
    const error = err as Error;
    logger?.error('[createUserOrganization] Failed to create organization', { message: error.message });
    return rejectWithValue({ message: error.message });
  }
});

export const updateOrganization = createAppAsyncThunk<CreateItemResponse<IOrganization>, { item: IOrganization }>(
  'organizations/updateOrganization',
  async ({ item }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;

    try {
      logger?.info('[updateOrganization] Starting organization update for userId:', item.ownerID);

      // await sleep(2000); // Simulate network delay for better UX during onboarding
      // return profile;

      const endpoint = getApiEndpoints().core.organizations.updateOrganization;
      return await apiService.put<CreateItemResponse<IOrganization>>(endpoint, item);
    } catch (err) {
      const error = err as Error;
      logger?.error('[updateOrganization] Failed to update organization', {
        ownerId: item.ownerID,
        message: error.message,
      });
      return rejectWithValue({ message: error.message });
    }
  },
);

export const checkSlug = createAppAsyncThunk<CheckSlugResult, { slug: string }, { rejectValue: { message: string } }>(
  'organizations/checkSlug',
  async ({ slug }, { extra: { config }, rejectWithValue }) => {
    const { apiService, logger } = config;
    const slugKey = toSlugKey(slug);

    try {
      const qs = new URLSearchParams({ slug: slugKey });
      const endpoint = `${getApiEndpoints().core.organizations.checkSlug}?${qs.toString()}`;

      logger?.info('[checkSlug] Checking slug:', slugKey);
      return await apiService.get<CheckSlugResult>(endpoint);
    } catch (err) {
      const error = err as Error & { status?: number; body?: string };

      // Backends sometimes return NotFound when slug is available.
      if (isNotFoundError(error)) {
        return { slug: slugKey, available: true };
      }

      // Conflict means slug already exists.
      if (error.status === 409) {
        return { slug: slugKey, available: false };
      }

      logger?.error('[checkSlug] Error:', error.message);
      return rejectWithValue({ message: error.message });
    }
  },
  {
    condition: ({ slug }, { getState }) => {
      const slugKey = toSlugKey(slug);
      if (!slugKey) return false;

      const state = getState().organizations;
      if (state.slugChecks[slugKey]) return false;
      if (state.slugChecksLoading[slugKey]) return false;
      if (state.slugChecksError[slugKey]) return false;

      return true;
    },
  },
);
